import React, { useState, useEffect, useRef, useCallback } from "react";
import { Stage, Layer, Rect, Image } from "react-konva";
import { useNavigate } from "react-router-dom";
// MUI
import { useMediaQuery } from "@mui/material";

function NavigationViewWithGrid(props) {
  const {
    pages,
    navigation,
    image,
    isActive,
    parentIsActive,
    section,
    currentPageId = null,
    imgSize = null,
  } = props;
  const navigate = useNavigate();

  const containerRef = useRef(null);

  const STAGE_WIDTH = 480 - 49; // 49 accounts for the height of the TabBar plus the bottom border
  const STAGE_HEIGHT = STAGE_WIDTH;
  const GRID_ROWS = 22;
  const GRID_COLS = 22;
  const CELL_WIDTH = STAGE_WIDTH / GRID_COLS;
  const CELL_HEIGHT = STAGE_HEIGHT / GRID_ROWS;

  const [backgroundImage, setBackgroundImage] = useState(null);
  const [containerSize, setContainerSize] = useState({
    width: STAGE_WIDTH,
    height: STAGE_HEIGHT,
  });
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const [clickableFields, setClickableFields] = useState(() =>
    buildFieldList()
  );

  const updateSize = useCallback(() => {
    if (!containerRef.current) return;

    const { clientWidth, clientHeight } = containerRef.current;

    setContainerSize({ width: clientWidth, height: clientHeight });
    setScale(Math.min(clientWidth / STAGE_WIDTH, clientHeight / STAGE_HEIGHT));
  }, [setContainerSize, setScale]);

  // Load image
  useEffect(() => {
    const img = new window.Image();
    const prefix = section.lesson.label.substring(0, 2).toUpperCase();
    img.src = `https://base-media-repo.web.app/${prefix}/${section.lesson.label}/images/paths/${image}.jpg`;
    img.onload = () => setBackgroundImage(img);
  }, [currentPageId]);

  // Set up resizing of window
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      if (!entries.length) return;

      const { width, height } = entries[0].contentRect;
      setContainerSize({ width, height });
      setScale(Math.min(width / STAGE_WIDTH, height / STAGE_HEIGHT));
    });

    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, [updateSize]);

  useEffect(() => {
    if (isActive && containerRef.current && parentIsActive) {
      updateSize();
    } else {
      setPosition({ x: 0, y: 0 });
      setScale(1);
    }
  }, [isActive, parentIsActive, updateSize]);

  function clampPosition(x, y) {
    const scaledWidth = STAGE_WIDTH * scale;
    const scaledHeight = STAGE_HEIGHT * scale;

    const minX = -(scaledWidth - containerSize.width);
    const minY = -(scaledHeight - containerSize.height);

    return {
      x: Math.min(0, Math.max(minX, x)),
      y: Math.min(0, Math.max(minY, y)),
    };
  }

  function buildFieldList() {
    let tempFields = [];

    if (navigation) {
      const events = navigation.events;
      for (let i = 0; i < events.length; i++) {
        const eventsItem = events[i];

        const page = pages.find((item) => item.id === eventsItem.page_id);

        let field = {
          page: page.id,
          page_label: page.label,
          type: eventsItem.type,
          column: Number(eventsItem.column),
          row: Number(eventsItem.row),
        };

        tempFields.push(field);
      }
    }

    return tempFields;
  }

  function handleZoom(e) {
    e.evt.preventDefault();
    e.evt.stopPropagation();

    const scaleBy = 1.1;
    const stage = e.target.getStage();
    const oldScale = scale;
    const pointer = stage.getPointerPosition();

    // **Calculate the minimum scale based on initial container size**
    const minScale = Math.min(
      containerSize.width / STAGE_WIDTH,
      containerSize.height / STAGE_HEIGHT
    );

    // Calculate new scale
    const newScale = e.evt.deltaY > 0 ? oldScale / scaleBy : oldScale * scaleBy;
    const clampedScale = Math.max(minScale, Math.min(2, newScale));

    // Get current stage position
    const stageX = stage.x();
    const stageY = stage.y();

    // Adjust position so zoom centers on the mouse pointer
    const mousePointTo = {
      x: (pointer.x - stageX) / oldScale,
      y: (pointer.y - stageY) / oldScale,
    };

    const newPos = {
      x: pointer.x - mousePointTo.x * clampedScale,
      y: pointer.y - mousePointTo.y * clampedScale,
    };

    setScale(clampedScale);
    setPosition(clampPosition(newPos.x, newPos.y));
  }

  const [value, setValue] = React.useState(0);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  return (
    <div
      ref={containerRef}
      id="navigation-view-with-grid"
      className="relative overflow-hidden"
      style={{
        height: "100%",
        width: "100%",
        maxHeight: STAGE_HEIGHT,
        maxWidth: STAGE_WIDTH,
      }}
    >
      <Stage
        width={1000}
        height={1000}
        draggable
        scaleX={scale}
        scaleY={scale}
        x={position.x}
        y={position.y}
        onDragMove={(e) => {
          const newPos = clampPosition(e.target.x(), e.target.y());
          e.target.x(newPos.x);
          e.target.y(newPos.y);
        }}
        onDragEnd={(e) => {
          const newPos = clampPosition(e.target.x(), e.target.y());
          setPosition(newPos);
        }}
        onWheel={(e) => handleZoom(e)}
      >
        <Layer>
          {backgroundImage && (
            <Image
              image={backgroundImage}
              width={STAGE_WIDTH}
              height={STAGE_HEIGHT}
            />
          )}

          {/* Render Grid */}
          {Array.from({ length: GRID_ROWS }).map((_, row) =>
            Array.from({ length: GRID_COLS }).map((_, col) => {
              const matchingField = clickableFields.find(
                (field) => field.column === col && field.row === row
              );

              const isClickable = !!matchingField;
              const pageId = matchingField?.page || null;

              const cellX = col * CELL_WIDTH;
              const cellY = row * CELL_HEIGHT;
              const cellKey = `${row}-${col}`;

              return (
                <Rect
                  key={cellKey}
                  x={cellX}
                  y={cellY}
                  width={CELL_WIDTH}
                  height={CELL_HEIGHT}
                  fill={
                    currentPageId !== null && currentPageId == pageId
                      ? "orange"
                      : "rgba(240, 240, 240, 0.0)"
                  }
                  // stroke="rgba(20, 17, 17, 0.99)"
                  // strokeWidth={1}
                  cornerRadius={4}
                  onMouseEnter={(e) => {
                    if (isClickable) {
                      document.body.style.cursor = "pointer";
                      e.target.fill("rgba(181, 8, 187, 0.8)");
                    } else {
                      document.body.style.cursor = "default";
                      e.target.fill(
                        currentPageId !== null && currentPageId == pageId
                          ? "orange"
                          : "rgba(240, 240, 240, 0.0)"
                      );
                    }
                  }}
                  onMouseLeave={(e) => {
                    document.body.style.cursor = "default";
                    e.target.fill(
                      currentPageId !== null && currentPageId == pageId
                        ? "orange"
                        : "rgba(240, 240, 240, 0.0)"
                    );
                  }}
                  onClick={(e) => {
                    if (e.evt.button === 0 && isClickable) {
                      navigate(`/pages/${pageId}`);
                    }
                  }}
                />
              );
            })
          )}
        </Layer>
      </Stage>
    </div>
  );
}

export default NavigationViewWithGrid;
